/**
 * The `parent_organizations` collection, loaded and indexed for resolution.
 *
 * One query per mounting surface, deliberately uncached. The bug this read
 * cutover fixes is a rename not showing up; a session cache here would bring
 * that straight back for the length of the session. Two dozen documents per tab
 * open is cheap.
 *
 * Fails soft: on error the index is empty and `error` is set, so a caller can
 * say "organizations unavailable" instead of flagging every value unmatched.
 */
import { useCallback, useEffect, useState } from 'react';
import { getParentOrganizations } from '../../services/firebase';
import {
  buildParentOrgIndex,
  EMPTY_PARENT_ORG_INDEX,
  type ParentOrgIndex,
} from './parentOrgResolve';

export function useParentOrgIndex(options: {
  /**
   * Load on mount (default). Pass false when the caller folds `reload` into its
   * own refresh that already runs on mount, or the collection is read twice.
   */
  autoLoad?: boolean;
} = {}): {
  index: ParentOrgIndex;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
} {
  const [index, setIndex] = useState<ParentOrgIndex>(EMPTY_PARENT_ORG_INDEX);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setIndex(buildParentOrgIndex(await getParentOrganizations()));
      setError(false);
    } catch (err) {
      console.warn('Parent organizations unavailable:', err);
      setIndex(EMPTY_PARENT_ORG_INDEX);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const autoLoad = options.autoLoad ?? true;
  useEffect(() => {
    if (autoLoad) void reload();
  }, [reload, autoLoad]);

  return { index, loading, error, reload };
}
